import { AutoComplete, DropdownLayoutValueOption } from '@wix/design-system'
import { ChangeEventHandler, useCallback, useEffect, useReducer, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'

import { usePageLogger } from '../../../bi/mfi'
import { LARGE_DESKTOP } from '../../../breakpoints'
import { BusinessTermService } from './api/BusinessTermService'
import { getInitialState, reducer, selectCategory } from './state'
import type { StateCategory } from './state/types'

export const useCategoryHandler = ({
  savedCategory,
  prefilledInput,
  questionKey,
  businessTermService,
}: {
  businessTermService: BusinessTermService
  savedCategory?: StateCategory
  questionKey: string
  prefilledInput: string
}) => {
  const autoCompleteRef = useRef<AutoComplete>(null)
  const [state, dispatch] = useReducer(reducer, { savedCategory, prefilledInput }, getInitialState)
  const { search, categories } = state

  const isLargeDesktopScreen = useMediaQuery(LARGE_DESKTOP)

  const pageLogger = usePageLogger()

  const handleSuggestionClick = useCallback(
    (suggestion: string, index: number) => {
      void pageLogger.clickAnswer({ questionKey, buttonName: suggestion, suggestedOption: true, position: index + 1 })
      dispatch({ type: 'selectSuggestion', suggestion })
      autoCompleteRef.current?.showOptions()
    },
    [pageLogger, questionKey],
  )

  const handleSearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    e => dispatch({ type: 'changeSearch', search: e.target.value }),
    [],
  )
  const handleClear = useCallback(() => {
    void pageLogger.generalAction({ action: 'delete_category' })
    dispatch({ type: 'changeSearch', search: '' })
  }, [pageLogger])

  const handleSearchSelect = useCallback(
    (option: DropdownLayoutValueOption) => {
      void selectCategory({
        dispatch,
        displayName: option.id as string,
        search,
        categories,
        pageLogger,
        questionKey,
        businessTermService,
      })
    },
    [categories, pageLogger, questionKey, search, businessTermService],
  )

  const handleInputClick = useCallback(() => autoCompleteRef.current?.showOptions(), [])

  useEffect(() => {
    autoCompleteRef.current?.focus()
    autoCompleteRef.current?.showOptions()
  }, [])

  return {
    state,
    dispatch,
    pageLogger,
    autoCompleteRef,
    handlers: {
      handleClear,
      handleInputClick,
      handleSearchSelect,
      handleSearchChange,
      handleSuggestionClick,
    },
    isLargeDesktopScreen,
  }
}
